import { AdminOrg, OrgMember, OrgStatus, OrgTier } from 'new/stores/adminOrgs/adminOrgs.types';

export const ORG_STATUS_LABEL: Record<OrgStatus, string> = {
  ACTIVE: 'Active',
  DEACTIVATED: 'Deactivated',
  SOURCE_COUNT_CAPPED: 'Source count capped',
  SOURCE_DATA_CAPPED: 'Source data capped',
  TRIAL_EXPIRED: 'Trial expired',
};

export const ORG_STATUS_CHIP_COLOR: Record<
  OrgStatus,
  'success' | 'warning' | 'primary' | 'error' | 'disabled'
> = {
  ACTIVE: 'success',
  DEACTIVATED: 'disabled',
  SOURCE_COUNT_CAPPED: 'warning',
  SOURCE_DATA_CAPPED: 'warning',
  TRIAL_EXPIRED: 'warning',
};

export function isOrgOnTrial(org: Pick<AdminOrg, 'org_tier'>) {
  return org?.org_tier?.name === 'TRIAL';
}

export type OrgFilterStatus = 'ACTIVE' | 'TRIAL' | 'SUSPENDED' | 'DEACTIVATED';

export const ORG_FILTER_STATUS_LABEL: Record<OrgFilterStatus, string> = {
  ACTIVE: 'Active',
  TRIAL: 'Trial',
  SUSPENDED: 'Suspended',
  DEACTIVATED: 'Deactivated',
};

/**
 * The backend's `status` enum (ACTIVE/DEACTIVATED/SOURCE_COUNT_CAPPED/SOURCE_DATA_CAPPED/
 * TRIAL_EXPIRED) doesn't have a "Trial" or "Suspended" value on its own — trial-ness comes
 * from the org's tier, and the capped/expired statuses all mean "restricted" in practice.
 * This buckets the real fields into the 4 categories admins actually think in.
 */
export function getOrgFilterStatus(org: Pick<AdminOrg, 'status' | 'org_tier'>): OrgFilterStatus {
  if (org.status === 'DEACTIVATED') return 'DEACTIVATED';
  if (
    org.status === 'SOURCE_COUNT_CAPPED' ||
    org.status === 'SOURCE_DATA_CAPPED' ||
    org.status === 'TRIAL_EXPIRED'
  ) {
    return 'SUSPENDED';
  }
  if (isOrgOnTrial(org)) return 'TRIAL';
  return 'ACTIVE';
}

export function daysUntil(dateStr?: string | null): number | null {
  if (!dateStr) return null;
  const diffMs = new Date(dateStr).getTime() - new Date().getTime();
  return Math.ceil(diffMs / (24 * 60 * 60 * 1000));
}

export function formatCompactNumber(value?: number | null): string {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(
    value,
  );
}

export function formatBytes(bytes?: number | null): string {
  if (bytes == null) return '—';
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;
  return `${value.toFixed(value >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
}

export function formatLimit(value?: number | null): string {
  if (value == null) return '—';
  if (value < 0) return 'Unlimited';
  return formatCompactNumber(value);
}

export function sumAccountMetrics(metrics?: { data: { records: number; size: number } }[]) {
  if (!metrics?.length) return { records: 0, size: 0 };
  return metrics.reduce(
    (acc, point) => ({
      records: acc.records + (point.data?.records ?? 0),
      size: acc.size + (point.data?.size ?? 0),
    }),
    { records: 0, size: 0 },
  );
}

export function getMemberRoleLabel(member: Pick<OrgMember, 'is_admin?' | 'access_role'>): string {
  if (member['is_admin?'] || member.access_role === 'admin') return 'Admin';
  if (member.access_role === 'collaborator') return 'Collaborator';
  return 'Member';
}

/**
 * Flattens everything a user might reasonably type to find an org — name, domain, id,
 * plan name, and both the raw and bucketed status labels — into one lowercase haystack,
 * since the plan/status labels are computed, not literal AdminOrg fields the generic
 * useFilteredData/filterBy(get-by-path) helper could reach on its own.
 */
export function buildOrgSearchText(org: AdminOrg): string {
  return [
    org.name,
    org.email_domain,
    String(org.id),
    org.org_tier?.display_name,
    ORG_STATUS_LABEL[org.status],
    ORG_FILTER_STATUS_LABEL[getOrgFilterStatus(org)],
    org.owner?.full_name,
    org.owner?.email,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export function orgInitial(name?: string): string {
  return (name || '?').trim().charAt(0).toUpperCase() || '?';
}

/**
 * OrgTier has no free-text "description" field on the backend — this synthesizes a
 * meaningful subtext from the real limit fields instead of fabricating marketing copy.
 */
export function describeTierLimits(tier: OrgTier): string {
  const seats = formatLimit(tier.user_members_limit);
  const sources = formatLimit(tier.data_source_count_limit);
  const records = formatLimit(tier.record_count_limit);
  const period = tier.record_count_limit_time?.toLowerCase();
  return `${seats} seats · ${sources} sources · ${records} records/${period}`;
}

export interface PlanOption {
  value: string;
  title: string;
  subtitle?: string;
}

export type PlanCategory = 'PAID' | 'FREE' | 'TRIAL' | 'EXPRESS' | 'OTHER';

const PLAN_CATEGORY_ORDER: PlanCategory[] = ['PAID', 'FREE', 'TRIAL', 'EXPRESS', 'OTHER'];

export const PLAN_CATEGORY_LABEL: Record<PlanCategory, string> = {
  PAID: 'Paid',
  FREE: 'Free',
  TRIAL: 'Trial',
  EXPRESS: 'Express',
  OTHER: 'Other',
};

/**
 * Tier `name` isn't a fixed enum on the backend — real seed data includes dozens (e.g. a
 * whole EXPRESS_PRO_1K..10K series, several distinct FREE/TRIAL tiers). This buckets them
 * into the handful of categories admins actually think and filter in.
 */
export function getPlanCategory(tier: Pick<OrgTier, 'name'> | null | undefined): PlanCategory {
  const name = tier?.name?.toUpperCase() ?? '';
  if (name.startsWith('EXPRESS')) return 'EXPRESS';
  if (name === 'PAID') return 'PAID';
  if (name.includes('TRIAL')) return 'TRIAL';
  if (name.includes('FREE')) return 'FREE';
  return 'OTHER';
}

/** Category priority first (Paid, Free, Trial, Express, Other), then alphabetical within it. */
export function sortTiersByCategory(tiers: OrgTier[]): OrgTier[] {
  return [...tiers].sort((a, b) => {
    const orderDiff =
      PLAN_CATEGORY_ORDER.indexOf(getPlanCategory(a)) -
      PLAN_CATEGORY_ORDER.indexOf(getPlanCategory(b));
    if (orderDiff !== 0) return orderDiff;
    return a.display_name.localeCompare(b.display_name);
  });
}

/**
 * Several real tiers share a display name (e.g. more than one "Free"/"Trial" tier with
 * different limits/ids) — the subtitle is what disambiguates them, so every plan picker
 * (Create Org, the listing's plan filter, Plan & Limits' tier switcher) must show it.
 */
export function buildPlanOptions(tiers: OrgTier[]): PlanOption[] {
  return sortTiersByCategory(tiers).map((tier) => ({
    value: tier.name,
    title: tier.display_name,
    subtitle: describeTierLimits(tier),
  }));
}

export const PLAN_FILTER_ALL = '__all__';
export const PLAN_FILTER_NONE = '__none__';

function describePlanCategory(tiers: OrgTier[], category: PlanCategory): string | undefined {
  const names = tiers
    .filter((tier) => getPlanCategory(tier) === category)
    .map((tier) => tier.display_name);
  if (!names.length) return undefined;
  return names.length <= 3
    ? names.join(', ')
    : `${names.slice(0, 3).join(', ')} +${names.length - 3} more`;
}

/**
 * Legacy orgs can have a null org_tier — "All plans" already includes them (it doesn't
 * filter at all), but there was no way to isolate just those orgs. "No plan" does that.
 * The filter operates on category, not individual tier, since there's no value in
 * isolating "Express Pro 4K" specifically from "Express Pro 5K" when filtering a list.
 * Each category's subtitle lists the real tier names it covers, same two-line
 * title+subtitle treatment as every other plan picker in this feature.
 */
export function buildPlanFilterOptions(tiers: OrgTier[]): PlanOption[] {
  return [
    { value: PLAN_FILTER_ALL, title: 'All plans', subtitle: 'Every plan and status' },
    {
      value: 'PAID',
      title: PLAN_CATEGORY_LABEL.PAID,
      subtitle: describePlanCategory(tiers, 'PAID'),
    },
    { value: PLAN_FILTER_NONE, title: 'No plan', subtitle: 'Legacy orgs with no tier assigned' },
    {
      value: 'FREE',
      title: PLAN_CATEGORY_LABEL.FREE,
      subtitle: describePlanCategory(tiers, 'FREE'),
    },
    {
      value: 'TRIAL',
      title: PLAN_CATEGORY_LABEL.TRIAL,
      subtitle: describePlanCategory(tiers, 'TRIAL'),
    },
    {
      value: 'EXPRESS',
      title: PLAN_CATEGORY_LABEL.EXPRESS,
      subtitle: describePlanCategory(tiers, 'EXPRESS'),
    },
  ];
}

export function orgMatchesPlanFilter(org: Pick<AdminOrg, 'org_tier'>, planFilter: string): boolean {
  if (planFilter === PLAN_FILTER_ALL) return true;
  if (planFilter === PLAN_FILTER_NONE) return !org.org_tier;
  return getPlanCategory(org.org_tier) === planFilter;
}

export function buildStatusFilterOptions(): { value: StatusFilterValue; label: string }[] {
  return [
    { value: 'ALL', label: 'All' },
    { value: 'ACTIVE', label: ORG_FILTER_STATUS_LABEL.ACTIVE },
    { value: 'TRIAL', label: ORG_FILTER_STATUS_LABEL.TRIAL },
    { value: 'SUSPENDED', label: ORG_FILTER_STATUS_LABEL.SUSPENDED },
    { value: 'DEACTIVATED', label: ORG_FILTER_STATUS_LABEL.DEACTIVATED },
  ];
}

export type StatusFilterValue = 'ALL' | OrgFilterStatus;
